"use client";

import { motion, useMotionValue, useReducedMotion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const CLICKABLE_SELECTOR =
  'a, button, input, textarea, select, label, summary, [role="button"], [role="link"], [role="menuitem"], [role="tab"], .cursor-pointer';

const CURSOR_ARROW_SRC = "/cursor-arrow.png";

// Variante plus claire du dégradé hero : emerald/teal/green 500
const HERO_GREEN_GRADIENT = "linear-gradient(to right, #10b981, #14b8a6, #22c55e)";
const HERO_TURQUOISE_AURA_GRADIENT = "linear-gradient(135deg, #2dd4bf 0%, #14b8a6 45%, #06b6d4 100%)";

const CUSTOM_CURSOR_CLASS = "custom-cursor-active";

const ARROW_MASK_STYLES = {
  WebkitMaskImage: `url(${CURSOR_ARROW_SRC})`,
  maskImage: `url(${CURSOR_ARROW_SRC})`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
  WebkitMaskPosition: "0 0",
  maskPosition: "0 0",
} as const;

function setCustomCursorActive(active: boolean) {
  document.documentElement.classList.toggle(CUSTOM_CURSOR_CLASS, active);
}

function isPointerInsideViewport(clientX: number, clientY: number) {
  return (
    clientX >= 0 &&
    clientY >= 0 &&
    clientX <= window.innerWidth &&
    clientY <= window.innerHeight
  );
}

function isClickableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(CLICKABLE_SELECTOR));
}

export function CustomCursor() {
  const pathname = usePathname();
  const reducedMotion = useReducedMotion();
  const [enabled, setEnabled] = useState(false);
  const [inViewport, setInViewport] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pressing, setPressing] = useState(false);
  const lastPointer = useRef({ x: -100, y: -100 });

  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  const isAdminRoute = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminRoute || reducedMotion) {
      setEnabled(false);
      setCustomCursorActive(false);
      return;
    }

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");

    const updateEnabled = () => {
      const active = finePointer.matches;
      setEnabled(active);
      setCustomCursorActive(active);
      if (!active) setInViewport(false);
    };

    updateEnabled();
    finePointer.addEventListener("change", updateEnabled);

    return () => {
      finePointer.removeEventListener("change", updateEnabled);
      setCustomCursorActive(false);
    };
  }, [isAdminRoute, reducedMotion]);

  useEffect(() => {
    if (!enabled) return;

    const updatePointer = (clientX: number, clientY: number) => {
      lastPointer.current = { x: clientX, y: clientY };
      mouseX.set(clientX);
      mouseY.set(clientY);
      setInViewport(isPointerInsideViewport(clientX, clientY));
    };

    const onMouseMove = (event: MouseEvent) => {
      updatePointer(event.clientX, event.clientY);
    };

    const onMouseOver = (event: MouseEvent) => {
      setHovering(isClickableTarget(event.target));
      updatePointer(event.clientX, event.clientY);
    };

    const onMouseDown = () => setPressing(true);
    const onMouseUp = () => setPressing(false);

    const onMouseOut = (event: MouseEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && document.documentElement.contains(next)) return;
      setInViewport(false);
    };

    const onWheel = () => {
      const { x, y } = lastPointer.current;
      if (isPointerInsideViewport(x, y)) setInViewport(true);
    };

    const onScroll = () => {
      const { x, y } = lastPointer.current;
      if (isPointerInsideViewport(x, y)) setInViewport(true);
    };

    const onPointerRawUpdate = (event: Event) => {
      const pointerEvent = event as PointerEvent;
      updatePointer(pointerEvent.clientX, pointerEvent.clientY);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseover", onMouseOver, { passive: true });
    window.addEventListener("mouseout", onMouseOut);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    if ("onpointerrawupdate" in window) {
      window.addEventListener("pointerrawupdate", onPointerRawUpdate as EventListener);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseover", onMouseOver);
      window.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", onScroll);

      if ("onpointerrawupdate" in window) {
        window.removeEventListener("pointerrawupdate", onPointerRawUpdate as EventListener);
      }
    };
  }, [enabled, mouseX, mouseY]);

  if (!enabled) return null;

  const size = pressing ? 34 : hovering ? 46 : 40;

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[9999]">
      <motion.div
        className="absolute top-0 left-0 will-change-transform"
        style={{ x: mouseX, y: mouseY }}
      >
        <motion.div
          className="absolute top-0 left-0"
          style={{
            ...ARROW_MASK_STYLES,
            background: HERO_TURQUOISE_AURA_GRADIENT,
            filter: "blur(7px)",
          }}
          animate={{
            width: size,
            height: size,
            opacity: inViewport ? 0.72 : 0,
          }}
          transition={{ type: "spring", stiffness: 700, damping: 34, mass: 0.2 }}
        />

        <motion.div
          className="absolute top-0 left-0"
          style={{
            ...ARROW_MASK_STYLES,
            background: `radial-gradient(circle at 28% 22%, rgba(255, 255, 255, 0.38) 0%, transparent 48%), ${HERO_GREEN_GRADIENT}`,
            filter:
              "drop-shadow(0 0 6px rgba(45, 212, 191, 0.55)) drop-shadow(0 0 14px rgba(6, 182, 212, 0.28))",
          }}
          animate={{
            width: size,
            height: size,
            opacity: inViewport ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 700, damping: 34, mass: 0.2 }}
        />
      </motion.div>
    </div>
  );
}
